import { request } from "../../../services/api"
import type { BaseApiResponse, BasePagination } from "../../../types/api.ts"
import type { Document, DocumentRequestData } from "../types/document.types.ts"
import type {
  DocumentInitialData,
  DocumentTemplate,
  DocumentTemplateFile,
  DocumentTemplateMutateData
} from "../types/types.ts"
import type {
  AvailableBlocksData,
  DocumentPreviewData,
  GetDocumentInitialDataParams,
  GetTemplatesParams
} from "./services.types.ts"

const BASE_URL_V2 = "/proposal/v2"
const BASE_URL = "/proposal"

const PATHS = {
  // Templates
  getTemplates: `${BASE_URL_V2}/templates/`,
  createTemplate: `${BASE_URL_V2}/templates/`,
  previewTemplate: `${BASE_URL_V2}/templates/preview/`,
  availableBlocks: `${BASE_URL_V2}/templates/available-blocks/`,
  template: (id: string) => ({
    get: `${BASE_URL_V2}/templates/${id}/`,
    update: `${BASE_URL_V2}/templates/${id}/`,
    delete: `${BASE_URL_V2}/templates/${id}/`,
    duplicate: `${BASE_URL_V2}/templates/${id}/duplicate/`
  }),

  // Documents
  initialData: (templateId: string) =>
    `${BASE_URL_V2}/templates/${templateId}/prefill/`,
  preview: `${BASE_URL_V2}/initial-preview/`,

  // Proposal Document
  createProposalDocument: `${BASE_URL_V2}/proposals/`,
  proposalDocument: (id: string) => ({
    get: `${BASE_URL_V2}/proposals/${id}/`,
    update: `${BASE_URL_V2}/proposals/${id}/`,
    save: `${BASE_URL}/${id}/save-file/`
  }),

  // Contract Document
  createContractDocument: "/proposal/v2/contracts/",
  contractDocument: (id: string) => ({
    get: `/proposal/v2/contracts/${id}/`,
    update: `/proposal/v2/contracts/${id}/`,
    save: `/proposal/contracts/${id}/save-file/`
  }),

  appendixFiles: `${BASE_URL_V2}/appendix-files/`,
  blockImages: `${BASE_URL_V2}/block-images/`
}

// ── Templates ─────────────────────────────────────────────────────────────────

export const getTemplates = (params?: GetTemplatesParams) =>
  request.api.get<BasePagination<DocumentTemplate>, BaseApiResponse<void>>(
    PATHS.getTemplates,
    params
  )

export const createTemplate = (data: DocumentTemplateMutateData) =>
  request.api.post<DocumentTemplate, BaseApiResponse<void>>(
    PATHS.createTemplate,
    data
  )

export const previewTemplate = (data: DocumentTemplateMutateData) =>
  request.api.post<string>(PATHS.previewTemplate, data)

export const getAvailableBlocks = () =>
  request.api.get<AvailableBlocksData, BaseApiResponse<void>>(
    PATHS.availableBlocks
  )

export const getTemplateById = (id: string) =>
  request.api.get<DocumentTemplate, BaseApiResponse<void>>(
    PATHS.template(id).get
  )

export const updateTemplate = (id: string, data: DocumentTemplateMutateData) =>
  request.api.put<DocumentTemplate, BaseApiResponse<void>>(
    PATHS.template(id).update,
    data
  )

export const deleteTemplate = (id: string) =>
  request.api.delete<BaseApiResponse<null>>(PATHS.template(id).delete)

export const duplicateTemplate = (id: string) =>
  request.api.post<BaseApiResponse<DocumentTemplate>>(
    PATHS.template(id).duplicate
  )

// ── Documents ─────────────────────────────────────────────────────────────────

export const getDocumentInitialData = ({
  templateId,
  leadId
}: GetDocumentInitialDataParams) =>
  request.api.post<BaseApiResponse<DocumentInitialData>>(
    PATHS.initialData(templateId),
    { lead_id: leadId }
  )

export const getDocumentPreview = (data: DocumentPreviewData) =>
  request.api.post<string, BaseApiResponse<void>>(PATHS.preview, data)

// ── Proposal Document ─────────────────────────────────────────────────────────

export const saveProposalDocument = (id: string) =>
  request.api.post<BaseApiResponse<null>>(PATHS.proposalDocument(id).save)

export const createProposalDocument = (data: DocumentRequestData) =>
  request.api.post<BaseApiResponse<Document>>(
    PATHS.createProposalDocument,
    data
  )

export const getProposalDocumentById = (id: string) =>
  request.api.get<Document, BaseApiResponse<void>>(
    PATHS.proposalDocument(id).get
  )

export const updateProposalDocument = (id: string, data: Partial<Document>) =>
  request.api.patch<BaseApiResponse<Document>>(
    PATHS.proposalDocument(id).update,
    data
  )

// ── Contract Document ─────────────────────────────────────────────────────────

export const saveContractDocument = (id: string) =>
  request.api.post<BaseApiResponse<null>>(PATHS.contractDocument(id).save)

export const createContractDocument = (data: DocumentRequestData) =>
  request.api.post<BaseApiResponse<Document>>(
    PATHS.createContractDocument,
    data
  )

export const getContractDocumentById = (id: string) =>
  request.api.get<Document, BaseApiResponse<void>>(
    PATHS.contractDocument(id).get
  )

export const updateContractDocument = (id: string, data: Partial<Document>) =>
  request.api.patch<BaseApiResponse<Document>>(
    PATHS.contractDocument(id).update,
    data
  )

// ── Appendix Files ────────────────────────────────────────────────────────────

export const uploadAppendixFile = (file: File) => {
  const formData = new FormData()
  formData.append("file", file)

  return request.api.post<BaseApiResponse<DocumentTemplateFile>>(
    PATHS.appendixFiles,
    formData,
    { headers: { "Content-Type": "multipart/form-data" } }
  )
}

export const getAppendixFiles = () =>
  request.api.get<DocumentTemplateFile[], BaseApiResponse<void>>(
    PATHS.appendixFiles
  )

// ── Block Images ──────────────────────────────────────────────────────────────

export const uploadBlockImage = (file: File) => {
  const formData = new FormData()
  formData.append("file", file)

  return request.api.post<BaseApiResponse<DocumentTemplateFile>>(
    PATHS.blockImages,
    formData,
    { headers: { "Content-Type": "multipart/form-data" } }
  )
}

export const getBlockImages = () =>
  request.api.get<DocumentTemplateFile[], BaseApiResponse<void>>(
    PATHS.blockImages
  )
